import { Document, Page, View, Text, Link, StyleSheet } from '@react-pdf/renderer';
import type { Question, Recommendation } from '../data/schemas';
import type { ActionPlan, TestResult } from '../types/result';
import type { ConfidenceId, SeverityId } from '../types/ids';
import { PERSONAL_VISIBILITY_NOTICE } from '../data/visibilityModelCopy';
import { NO_TENSIONS_DETECTED_TEXT } from '../data/resultCopy';

const CONFIDENCE_LABEL: Record<ConfidenceId, string> = {
  low: 'niedrig',
  medium: 'mittel',
  high: 'hoch',
};

const SEVERITY_LABEL: Record<SeverityId, string> = {
  notice: 'Hinweis',
  important: 'Wichtiger Reibungspunkt',
  critical: 'Zentrale Voraussetzung ungeklärt',
};

const ACTION_PLAN_GROUPS: { key: keyof ActionPlan; title: string }[] = [
  { key: 'first', title: 'Zuerst' },
  { key: 'next', title: 'Danach' },
  { key: 'later', title: 'Später' },
];

const HEADER_TITLE = 'Wie gut passt Instagram zu deiner Sichtbarkeitsstrategie - Auswertung';
const BRAND_LABEL = 'SoloStarter.ONE';
const BRAND_URL = 'https://solostarter.one';

const styles = StyleSheet.create({
  page: {
    paddingTop: 60,
    paddingBottom: 56,
    paddingHorizontal: 44,
    fontSize: 10,
    color: '#112027',
    fontFamily: 'Helvetica',
  },
  header: {
    position: 'absolute',
    top: 24,
    left: 44,
    right: 44,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#c3d3d5',
    paddingBottom: 8,
  },
  headerText: {
    fontSize: 9,
    color: '#46595f',
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 44,
    right: 44,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#c3d3d5',
    paddingTop: 8,
  },
  footerBrand: {
    fontSize: 9,
    color: '#0a6a62',
    textDecoration: 'none',
  },
  footerPage: {
    fontSize: 9,
    color: '#46595f',
  },
  coverPage: {
    paddingHorizontal: 56,
    paddingVertical: 80,
    fontFamily: 'Helvetica',
    color: '#112027',
    justifyContent: 'space-between',
  },
  coverBrand: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: '#0a6a62',
  },
  coverTitle: {
    fontSize: 26,
    fontFamily: 'Helvetica-Bold',
    marginTop: 24,
    marginBottom: 16,
    lineHeight: 1.3,
  },
  coverSubtitle: {
    fontSize: 13,
    color: '#46595f',
    marginBottom: 24,
    lineHeight: 1.5,
  },
  coverScoreBox: {
    borderWidth: 1,
    borderColor: '#c3d3d5',
    borderRadius: 6,
    padding: 20,
    alignItems: 'center',
    marginBottom: 24,
  },
  coverScoreValue: {
    fontSize: 36,
    fontFamily: 'Helvetica-Bold',
    color: '#0a6a62',
  },
  coverMeta: {
    fontSize: 10,
    color: '#46595f',
  },
  h1: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 8,
  },
  h2: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    marginTop: 20,
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#c3d3d5',
    paddingBottom: 4,
  },
  h3: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 3,
  },
  paragraph: {
    marginBottom: 6,
    lineHeight: 1.4,
  },
  muted: {
    color: '#46595f',
  },
  card: {
    borderWidth: 1,
    borderColor: '#c3d3d5',
    borderRadius: 4,
    padding: 10,
    marginBottom: 8,
  },
  cardHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  pill: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: '#0a6a62',
    marginBottom: 4,
  },
  listItem: {
    marginBottom: 2,
    lineHeight: 1.4,
  },
});

interface BulletListProps {
  items: readonly string[];
}

function BulletList({ items }: BulletListProps) {
  return (
    <View>
      {items.map((item) => (
        <Text key={item} style={styles.listItem}>
          {'• '}
          {item}
        </Text>
      ))}
    </View>
  );
}

/** Wiederholt sich auf jeder Inhaltsseite (nicht auf dem Deckblatt), da innerhalb der Page `fixed`. */
function PdfHeader() {
  return (
    <View style={styles.header} fixed>
      <Text style={styles.headerText}>{HEADER_TITLE}</Text>
    </View>
  );
}

function PdfFooter() {
  return (
    <View style={styles.footer} fixed>
      <Link src={BRAND_URL} style={styles.footerBrand}>
        {BRAND_LABEL}
      </Link>
      <Text
        style={styles.footerPage}
        render={({ pageNumber, totalPages }) => `Seite ${pageNumber} von ${totalPages}`}
      />
    </View>
  );
}

interface ResultPdfDocumentProps {
  result: TestResult;
  recommendations: readonly Recommendation[];
  questions: readonly Question[];
  maximumFeaturedTensions: number;
  showClusterDetailSection: boolean;
  showWeightingDetails: boolean;
}

/** Ergebnisbericht als eigenständiges PDF-Dokument mit echtem, durchsuchbarem Text. */
export function ResultPdfDocument({
  result,
  recommendations,
  questions,
  maximumFeaturedTensions,
  showClusterDetailSection,
  showWeightingDetails,
}: ResultPdfDocumentProps) {
  const featuredTensions = result.tensions.slice(0, maximumFeaturedTensions);
  const remainingTensions = result.tensions.slice(maximumFeaturedTensions);
  const hasActionPlan = ACTION_PLAN_GROUPS.some((group) => result.actionPlan[group.key].length > 0);
  const createdAtLabel = new Date(result.createdAt).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  function recommendationTitles(ids: readonly string[]): string[] {
    return ids
      .map((id) => recommendations.find((r) => r.id === id))
      .filter((r): r is Recommendation => !!r)
      .map((r) => r.title);
  }

  return (
    <Document
      title={result.summary.headline}
      subject="Passt Instagram als Sichtbarkeitskanal? – Ergebnisbericht"
    >
      <Page size="A4" style={styles.coverPage}>
        <View>
          <Text style={styles.coverBrand}>{BRAND_LABEL}</Text>
          <Text style={styles.coverTitle}>{HEADER_TITLE}</Text>
          <Text style={styles.coverSubtitle}>{result.summary.headline}</Text>
        </View>

        <View style={styles.coverScoreBox}>
          <Text style={styles.coverScoreValue}>
            {result.overall.score} / {result.overall.maximum}
          </Text>
          <Text style={styles.paragraph}>
            {result.overall.percentage} % · {result.overall.label}
          </Text>
        </View>

        <View>
          <Text style={styles.coverMeta}>Erstellt am {createdAtLabel}</Text>
          <Link src={BRAND_URL} style={[styles.coverMeta, { marginTop: 4 }]}>
            {BRAND_URL}
          </Link>
        </View>
      </Page>

      <Page size="A4" style={styles.page}>
        <PdfHeader />

        <Text style={styles.h1}>{result.summary.headline}</Text>

        <View style={styles.coverScoreBox}>
          <Text style={styles.coverScoreValue}>
            {result.overall.score} / {result.overall.maximum}
          </Text>
          <Text style={styles.paragraph}>
            {result.overall.percentage} % · {result.overall.label}
          </Text>
        </View>

        <Text style={styles.paragraph}>{result.summary.summaryText}</Text>
        <Text style={styles.paragraph}>
          <Text style={{ fontFamily: 'Helvetica-Bold' }}>Wichtigste Voraussetzung: </Text>
          {result.summary.mainCondition}
        </Text>
        {result.summary.mainTension && (
          <Text style={styles.paragraph}>
            <Text style={{ fontFamily: 'Helvetica-Bold' }}>Wichtigstes Spannungsfeld: </Text>
            {result.summary.mainTension}
          </Text>
        )}

        <Text style={styles.h2}>Deine sechs Bewertungsbereiche</Text>
        {result.clusters.map((cluster) => (
          <View key={cluster.clusterId} style={styles.card} wrap={false}>
            <View style={styles.cardHeaderRow}>
              <Text style={styles.h3}>{cluster.title}</Text>
              <Text style={styles.h3}>{cluster.percentage} %</Text>
            </View>
            <Text style={styles.pill}>{cluster.label}</Text>
            <Text style={styles.paragraph}>{cluster.summary}</Text>
          </View>
        ))}

        <Text style={styles.h2}>Spannungsfelder</Text>
        {result.tensions.length === 0 ? (
          <Text style={[styles.paragraph, styles.muted]}>{NO_TENSIONS_DETECTED_TEXT}</Text>
        ) : (
          <>
            {[...featuredTensions, ...remainingTensions].map((tension) => (
              <View key={tension.ruleId} style={styles.card} wrap={false}>
                <Text style={styles.pill}>Spannungsfeld · {SEVERITY_LABEL[tension.severity]}</Text>
                <Text style={styles.h3}>{tension.title}</Text>
                <Text style={styles.paragraph}>{tension.summary}</Text>
                <Text style={styles.paragraph}>
                  <Text style={{ fontFamily: 'Helvetica-Bold' }}>Warum das wichtig ist: </Text>
                  {tension.explanation}
                </Text>
                {tension.businessImpact && (
                  <Text style={styles.paragraph}>
                    <Text style={{ fontFamily: 'Helvetica-Bold' }}>Geschäftliche Auswirkung: </Text>
                    {tension.businessImpact}
                  </Text>
                )}
                {tension.personalImpact && (
                  <Text style={styles.paragraph}>
                    <Text style={{ fontFamily: 'Helvetica-Bold' }}>Persönliche Auswirkung: </Text>
                    {tension.personalImpact}
                  </Text>
                )}
                {recommendationTitles(tension.recommendationIds).length > 0 && (
                  <>
                    <Text style={styles.h3}>Passende Handlungsschritte</Text>
                    <BulletList items={recommendationTitles(tension.recommendationIds)} />
                  </>
                )}
              </View>
            ))}
          </>
        )}

        <Text style={styles.h2}>Stärken</Text>
        {result.strengths.length === 0 ? (
          <Text style={[styles.paragraph, styles.muted]}>
            Aktuell wurden keine besonders herausragenden Einzelstärken erkannt.
          </Text>
        ) : (
          result.strengths.map((insight) => (
            <View key={insight.id} style={styles.card} wrap={false}>
              <Text style={styles.h3}>{insight.title}</Text>
              <Text style={styles.paragraph}>{insight.summary}</Text>
              <Text style={styles.paragraph}>{insight.detail}</Text>
            </View>
          ))
        )}

        <Text style={styles.h2}>Wichtigste Engpässe</Text>
        {result.weakPoints.length === 0 ? (
          <Text style={[styles.paragraph, styles.muted]}>
            Aktuell wurden keine besonders kritischen Einzel-Engpässe erkannt.
          </Text>
        ) : (
          result.weakPoints.map((insight) => (
            <View key={insight.id} style={styles.card} wrap={false}>
              <Text style={styles.h3}>{insight.title}</Text>
              <Text style={styles.paragraph}>{insight.summary}</Text>
              <Text style={styles.paragraph}>{insight.detail}</Text>
            </View>
          ))
        )}

        <Text style={styles.h2}>Empfohlene Rolle von Instagram</Text>
        <View style={styles.card} wrap={false}>
          <Text style={styles.pill}>{result.recommendedChannelRole.label}</Text>
          <Text style={styles.paragraph}>{result.recommendedChannelRole.summary}</Text>
          <Text style={styles.paragraph}>{result.recommendedChannelRole.explanation}</Text>
          {result.recommendedChannelRole.suitableUses.length > 0 && (
            <>
              <Text style={styles.h3}>Geeignet für</Text>
              <BulletList items={result.recommendedChannelRole.suitableUses} />
            </>
          )}
          {result.recommendedChannelRole.unsuitableUses.length > 0 && (
            <>
              <Text style={[styles.h3, { marginTop: 4 }]}>Nicht geeignet für</Text>
              <BulletList items={result.recommendedChannelRole.unsuitableUses} />
            </>
          )}
          <Text style={[styles.paragraph, { marginTop: 4 }]}>
            Sicherheit dieser Empfehlung:{' '}
            {CONFIDENCE_LABEL[result.recommendedChannelRole.confidence]}
          </Text>
        </View>

        <Text style={styles.h2}>Empfohlenes Sichtbarkeitsmodell</Text>
        <View style={styles.card} wrap={false}>
          <Text style={styles.pill}>{result.recommendedVisibilityModel.label}</Text>
          <Text style={styles.paragraph}>{result.recommendedVisibilityModel.summary}</Text>
          <Text style={styles.paragraph}>{PERSONAL_VISIBILITY_NOTICE}</Text>
        </View>

        {showClusterDetailSection && (
          <>
            <Text style={styles.h2}>Ausführliche Analyse je Bereich</Text>
            {result.clusters.map((cluster) => (
              <View key={cluster.clusterId} style={styles.card} wrap={false}>
                <View style={styles.cardHeaderRow}>
                  <Text style={styles.h3}>{cluster.title}</Text>
                  <Text style={styles.h3}>{cluster.percentage} %</Text>
                </View>
                <Text style={styles.pill}>{cluster.label}</Text>
                <Text style={styles.paragraph}>{cluster.detail}</Text>
                {cluster.opportunities.length > 0 && (
                  <>
                    <Text style={styles.h3}>Chancen</Text>
                    <BulletList items={cluster.opportunities} />
                  </>
                )}
                {cluster.risks.length > 0 && (
                  <>
                    <Text style={[styles.h3, { marginTop: 4 }]}>Risiken</Text>
                    <BulletList items={cluster.risks} />
                  </>
                )}
                {showWeightingDetails && (
                  <>
                    <Text style={[styles.h3, { marginTop: 4 }]}>
                      Beteiligte Fragen und Einfluss auf diesen Bereich
                    </Text>
                    <BulletList
                      items={cluster.contributingQuestions.map((contribution) => {
                        const question = questions.find((q) => q.id === contribution.questionId);
                        const weightPercentage = Math.round(contribution.weight * 100);
                        return `${question?.title}: ${contribution.answerScore} von 2 Punkten (Einfluss auf diesen Bereich: ${weightPercentage} %)`;
                      })}
                    />
                  </>
                )}
              </View>
            ))}
          </>
        )}

        <Text style={styles.h2}>Nächste Schritte</Text>
        {!hasActionPlan ? (
          <Text style={[styles.paragraph, styles.muted]}>
            Aktuell ergeben sich aus deinen Antworten keine zusätzlichen priorisierten
            Handlungsschritte.
          </Text>
        ) : (
          ACTION_PLAN_GROUPS.map((group) => {
            const ids = result.actionPlan[group.key];
            if (ids.length === 0) return null;
            return (
              <View key={group.key} wrap={false}>
                <Text style={styles.h3}>{group.title}</Text>
                {ids.map((id) => {
                  const recommendation = recommendations.find((r) => r.id === id);
                  if (!recommendation) return null;
                  return (
                    <View key={id} style={styles.card} wrap={false}>
                      <Text style={styles.h3}>{recommendation.title}</Text>
                      <Text style={styles.paragraph}>{recommendation.description}</Text>
                      <BulletList items={recommendation.actionItems} />
                    </View>
                  );
                })}
              </View>
            );
          })
        )}

        <Text style={[styles.paragraph, styles.muted, { marginTop: 12 }]}>
          Dieses Ergebnis bewertet die strukturelle Eignung von Instagram für dein Angebot. Es ist
          keine Erfolgsgarantie und keine Bewertung deiner Person.
        </Text>

        <PdfFooter />
      </Page>
    </Document>
  );
}
